/**
 * 渠道管理（管理员）。
 *
 * 渠道 CRUD + 健康状态展示 + 手动探活。
 *
 * 注：当前后端服务（services/admin.ChannelInput）未包含 apiKey 字段，
 * 对话框中展示的“密钥”仅作 UI 占位，不会随请求提交（保持 service 契约不变）。
 *
 * @module pages/admin/ChannelAdminPage
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import PageHeader from '@/components/common/PageHeader';
import DataTable, { type Column } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import { useApi } from '@/hooks/useApi';
import {
  createChannel,
  deleteChannel,
  listChannels,
  triggerChannelHealth,
  updateChannel,
  type ChannelInput,
} from '@/services/admin';
import type { Channel, ChannelHealth } from '@/types';

/** 健康状态元信息。 */
const HEALTH_META: Record<ChannelHealth, { label: string; color: 'success' | 'warning' | 'error' }> = {
  healthy: { label: '健康', color: 'success' },
  degraded: { label: '降级', color: 'warning' },
  down: { label: '故障', color: 'error' },
};

/** 表单状态。 */
interface ChannelForm {
  name: string;
  type: string;
  baseUrl: string;
  apiKey: string;
  modelList: string;
  priority: string;
  enabled: boolean;
}

/** 空表单。 */
const EMPTY_FORM: ChannelForm = {
  name: '',
  type: '',
  baseUrl: '',
  apiKey: '',
  modelList: '',
  priority: '10',
  enabled: true,
};

/**
 * 渠道管理页组件。
 *
 * @returns 页面元素。
 */
export default function ChannelAdminPage(): JSX.Element {
  const { data, loading, refetch } = useApi(() => listChannels());
  const { show, Toast } = useToast();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Channel | null>(null);
  const [form, setForm] = useState<ChannelForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [probing, setProbing] = useState(false);

  const openCreate = (): void => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (channel: Channel): void => {
    setEditing(channel);
    setForm({
      name: channel.name,
      type: channel.type,
      baseUrl: channel.baseUrl,
      apiKey: '',
      modelList: channel.modelList.join(', '),
      priority: String(channel.priority),
      enabled: channel.enabled,
    });
    setOpen(true);
  };

  const handleSave = async (): Promise<void> => {
    if (!form.name.trim() || !form.baseUrl.trim()) {
      show('请填写名称与 Base URL', 'warning');
      return;
    }
    const input: ChannelInput = {
      name: form.name.trim(),
      type: form.type.trim(),
      baseUrl: form.baseUrl.trim(),
      modelList: form.modelList
        .split(',')
        .map((m) => m.trim())
        .filter(Boolean),
      priority: Number(form.priority) || 0,
      enabled: form.enabled,
    };
    setSaving(true);
    try {
      if (editing) {
        await updateChannel(editing.id, input);
        show('渠道已更新');
      } else {
        await createChannel(input);
        show('渠道已创建');
      }
      setOpen(false);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (deleteId == null) return;
    setDeleting(true);
    try {
      await deleteChannel(deleteId);
      show('渠道已删除');
      setDeleteId(null);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '删除失败', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleProbe = async (): Promise<void> => {
    setProbing(true);
    try {
      await triggerChannelHealth(0);
      show('已触发全部渠道探活', 'info');
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '探活失败', 'error');
    } finally {
      setProbing(false);
    }
  };

  const columns: Column<Channel>[] = [
    { key: 'name', header: '名称' },
    { key: 'type', header: '类型' },
    { key: 'baseUrl', header: 'Base URL' },
    {
      key: 'healthStatus',
      header: '健康状态',
      render: (c) => (
        <StatusChip
          label={HEALTH_META[c.healthStatus].label}
          color={HEALTH_META[c.healthStatus].color}
        />
      ),
    },
    {
      key: 'failCount',
      header: '连续失败',
      align: 'right',
      render: (c) => c.failCount,
    },
    {
      key: 'priority',
      header: '优先级',
      align: 'right',
      render: (c) => c.priority,
    },
    {
      key: 'enabled',
      header: '启用',
      render: (c) => <StatusChip label={c.enabled ? '已启用' : '已停用'} color={c.enabled ? 'success' : 'default'} />,
    },
    {
      key: 'actions',
      header: '操作',
      align: 'right',
      render: (c) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="编辑">
            <IconButton size="small" onClick={() => openEdit(c)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="删除">
            <IconButton size="small" color="error" onClick={() => setDeleteId(c.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="渠道管理"
        subtitle="管理上游模型渠道与调度优先级"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<CheckCircleIcon />} onClick={handleProbe} disabled={probing}>
              {probing ? '探活中...' : '手动探活'}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              新建渠道
            </Button>
          </Box>
        }
      />

      <DataTable
        columns={columns}
        rows={data ?? []}
        rowKey={(c) => c.id}
        loading={loading}
        emptyText="暂无渠道"
      />

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? '编辑渠道' : '新建渠道'}</DialogTitle>
        <DialogContent>
          <TextField label="名称" fullWidth margin="normal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField label="类型（如 openai/claude）" fullWidth margin="normal" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} />
          <TextField label="Base URL" fullWidth margin="normal" value={form.baseUrl} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} />
          <TextField
            label="密钥（占位，暂未接入后端）"
            type="password"
            fullWidth
            margin="normal"
            value={form.apiKey}
            onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
            helperText="当前后端 Channel 接口未包含 apiKey 字段，保存时不提交该值"
          />
          <TextField label="支持模型（逗号分隔）" fullWidth margin="normal" value={form.modelList} onChange={(e) => setForm({ ...form, modelList: e.target.value })} />
          <TextField label="优先级（数值越小越优先）" type="number" fullWidth margin="normal" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
          <FormControlLabel
            control={<Switch checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />}
            label="启用"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            取消
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteId != null}
        danger
        title="删除渠道"
        message="删除后该渠道将不再参与调度，确定删除？"
        confirmText="删除"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => !deleting && setDeleteId(null)}
      />
      {Toast}
    </Box>
  );
}
